import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  X,
  Mail,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Type,
  Palette,
  Link2,
  Minus,
  Undo2,
  Redo2,
} from "lucide-react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./app.css";
import { secretKey } from "./data";

const MySwal = withReactContent(Swal2);
const API = "https://apiftpsplit.ftpeople.pl";

/* ── toolbar config ── */
const FONT_SIZES = [
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
];
const COLORS = [
  "#000000",
  "#333333",
  "#ef4444",
  "#f59e0b",
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#ffffff",
];

const AddMail = ({ companies = [], user, close, editMail, mode }) => {
  const [company, setCompany] = useState(editMail?.company ?? "");
  const [subCompany, setSubCompany] = useState(editMail?.subCompany ?? "");
  const [subject, setSubject] = useState(editMail?.subject ?? "");
  const [name, setName] = useState(editMail?.name ?? "");
  const [saving, setSaving] = useState(false);

  /* toolbar dropdowns */
  const [showFontSize, setShowFontSize] = useState(false);
  const [showColor, setShowColor] = useState(false);
  const [showBgColor, setShowBgColor] = useState(false);

  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editMail) {
      editorRef.current.innerHTML = editMail.content;
    }
  }, []);

  /* ── execCommand wrapper ── */
  const exec = useCallback((cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  }, []);

  const closeDropdowns = () => {
    setShowFontSize(false);
    setShowColor(false);
    setShowBgColor(false);
  };

  const handleFontSize = (size) => {
    const sel = window.getSelection();
    if (!sel.rangeCount || sel.isCollapsed) {
      closeDropdowns();
      return;
    }

    // Użyj execCommand z tymczasowym fontSize, potem podmień na span
    exec("fontSize", "7");

    // Znajdź wszystkie font[size="7"] i zamień na span z fontSize
    const fonts = editorRef.current?.querySelectorAll('font[size="7"]');
    fonts?.forEach((font) => {
      const span = document.createElement("span");
      span.style.fontSize = size;
      span.innerHTML = font.innerHTML;
      font.parentNode.replaceChild(span, font);
    });

    closeDropdowns();
    editorRef.current?.focus();
  };

  const handleColor = (color) => {
    exec("foreColor", color);
    closeDropdowns();
  };

  const handleBgColor = (color) => {
    exec("hiliteColor", color);
    closeDropdowns();
  };

  const insertLink = () => {
    const url = prompt("Podaj URL:");
    if (url) exec("createLink", url);
  };

  /* ── save ── */
  const getHtml = () => editorRef.current?.innerHTML || "";

  const handleSave = async () => {
    if (!company) {
      MySwal.fire({ icon: "error", title: "Wypełnij", text: "Wybierz dział" });
      return;
    }
    if (!subject.trim()) {
      MySwal.fire({
        icon: "error",
        title: "Wypełnij",
        text: "Wpisz temat wiadomości",
      });
      return;
    }
    const html = getHtml();
    if (!html || html === "<br>") {
      MySwal.fire({
        icon: "error",
        title: "Wypełnij",
        text: "Wpisz zawartość wiadomości",
      });
      return;
    }

    setSaving(true);
    try {
      var endpoint = mode === "edit" ? "/editMail" : "/addMail";
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: secretKey,
        },
        body: JSON.stringify({
          company,
          subject: subject.trim(),
          body: html,
          name: name,
          subCompany,
          id: editMail?.id ?? "",
        }),
      });
      const data = await res.json();
      if (data.success) {
        MySwal.fire({
          icon: "success",
          title: "Zapisano",
          text: `Szablon został ${mode === "edit" ? "edytowany" : "dodany"}`,
        });
        close();
      } else {
        MySwal.fire({
          icon: "error",
          title: "Błąd",
          text: data.message || "Nie udało się zapisać",
        });
      }
    } catch (err) {
      console.error(mode === "edit" ? "editMail:" : "addMail:", err);
      MySwal.fire({
        icon: "error",
        title: "Błąd",
        text: "Wystąpił błąd połączenia",
      });
    }
    setSaving(false);
  };

  const delMail = async () => {
    setSaving(true);
    try {
      var endpoint = "/deleteMail";
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: secretKey,
        },
        body: JSON.stringify({
          id: editMail?.id ?? "",
        }),
      });
      const data = await res.json();
      if (data.success) {
        MySwal.fire({
          icon: "success",
          title: "Zapisano",
          text: `Szablon został usunięty`,
        });
        close();
      } else {
        MySwal.fire({
          icon: "error",
          title: "Błąd",
          text: data.message || "Nie udało się usunąć",
        });
      }
    } catch (err) {
      console.error("delMail:", err);
      MySwal.fire({
        icon: "error",
        title: "Błąd",
        text: "Wystąpił błąd połączenia",
      });
    }
    setSaving(false);
  };

  /* ── toolbar button ── */
  const TBtn = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      className={`et-tb-btn${active ? " et-tb-btn--active" : ""}`}
      onClick={onClick}
      title={title}
    >
      <Icon size={14} />
    </button>
  );

  return (
    <div className="sn-overlay">
      <div className="sn-modal" style={{ maxWidth: 720 }}>
        {/* Header */}
        <div className="sn-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Mail size={18} />
            <div>
              <h2 className="sn-title">Nowy szablon e-mail</h2>
              <span className="sn-subtitle">Użytkownik: {user.name}</span>
            </div>
          </div>
          <button className="ld-close" onClick={close}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="sn-body">
          {/* Dział */}
          <div
            className="db-filter-group"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <div style={{ width: "100%" }}>
              <p className="db-field-label">Dział</p>
              <select
                className="db-select"
                style={{ width: "100%" }}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              >
                <option value="">-- Wybierz dział --</option>
                {companies.map((c) => (
                  <option key={c.id ?? c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            {company === "SPRZEDAZ" ? (
              <div style={{ width: "100%" }}>
                <p className="db-field-label">Poddział</p>
                <select
                  className="db-select"
                  style={{ width: "100%" }}
                  value={subCompany}
                  onChange={(e) => setSubCompany(e.target.value)}
                >
                  <option value="">-- Wybierz dział --</option>
                  {companies.map((c) => (
                    <option key={c.id ?? c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              ""
            )}
          </div>

          {/* Nazwa */}
          <div className="db-filter-group">
            <label className="db-field-label">Nazwa</label>
            <input
              type="text"
              className="db-input"
              placeholder="Nazwa wiadomości... (roboczo)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Temat */}
          <div className="db-filter-group">
            <label className="db-field-label">Temat</label>
            <input
              type="text"
              className="db-input"
              placeholder="Temat wiadomości..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Edytor HTML */}
          <div className="db-filter-group">
            <label className="db-field-label">Treść wiadomości</label>

            {/* Toolbar */}
            <div className="et-toolbar" onClick={closeDropdowns}>
              <TBtn
                icon={Bold}
                onClick={() => exec("bold")}
                title="Pogrubienie"
              />
              <TBtn
                icon={Italic}
                onClick={() => exec("italic")}
                title="Kursywa"
              />
              <TBtn
                icon={Underline}
                onClick={() => exec("underline")}
                title="Podkreślenie"
              />
              <TBtn
                icon={Minus}
                onClick={() => exec("strikeThrough")}
                title="Przekreślenie"
              />

              <span className="et-tb-sep" />

              {/* Font size */}
              <div className="et-tb-dropdown-wrap">
                <button
                  type="button"
                  className="et-tb-btn"
                  title="Rozmiar czcionki"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFontSize((p) => !p);
                    setShowColor(false);
                    setShowBgColor(false);
                  }}
                >
                  <Type size={14} />
                </button>
                {showFontSize && (
                  <div
                    className="et-tb-dropdown"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {FONT_SIZES.map((s) => (
                      <button
                        key={s}
                        className="et-tb-dropdown-item"
                        onClick={() => handleFontSize(s)}
                      >
                        <span style={{ fontSize: s }}>{s}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Text color */}
              <div className="et-tb-dropdown-wrap">
                <button
                  type="button"
                  className="et-tb-btn"
                  title="Kolor tekstu"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowColor((p) => !p);
                    setShowFontSize(false);
                    setShowBgColor(false);
                  }}
                >
                  <Palette size={14} />
                </button>
                {showColor && (
                  <div
                    className="et-tb-dropdown et-tb-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        className="et-color-swatch"
                        style={{
                          background: c,
                          border: c === "#ffffff" ? "1px solid #ccc" : "none",
                        }}
                        onClick={() => handleColor(c)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Background color */}
              <div className="et-tb-dropdown-wrap">
                <button
                  type="button"
                  className="et-tb-btn"
                  title="Kolor tła"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowBgColor((p) => !p);
                    setShowFontSize(false);
                    setShowColor(false);
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 2 }}
                  >
                    <Palette size={14} />
                    <span style={{ fontSize: 9, fontWeight: 700 }}>BG</span>
                  </span>
                </button>
                {showBgColor && (
                  <div
                    className="et-tb-dropdown et-tb-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        className="et-color-swatch"
                        style={{
                          background: c,
                          border: c === "#ffffff" ? "1px solid #ccc" : "none",
                        }}
                        onClick={() => handleBgColor(c)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <span className="et-tb-sep" />

              <TBtn
                icon={AlignLeft}
                onClick={() => exec("justifyLeft")}
                title="Do lewej"
              />
              <TBtn
                icon={AlignCenter}
                onClick={() => exec("justifyCenter")}
                title="Wyśrodkuj"
              />
              <TBtn
                icon={AlignRight}
                onClick={() => exec("justifyRight")}
                title="Do prawej"
              />

              <span className="et-tb-sep" />

              <TBtn
                icon={List}
                onClick={() => exec("insertUnorderedList")}
                title="Lista"
              />
              <TBtn
                icon={ListOrdered}
                onClick={() => exec("insertOrderedList")}
                title="Lista numerowana"
              />
              <TBtn icon={Link2} onClick={insertLink} title="Wstaw link" />

              <span className="et-tb-sep" />

              <TBtn icon={Undo2} onClick={() => exec("undo")} title="Cofnij" />
              <TBtn icon={Redo2} onClick={() => exec("redo")} title="Ponów" />
            </div>

            {/* Editable area */}
            <div
              ref={editorRef}
              className="et-editor"
              contentEditable
              suppressContentEditableWarning
              data-placeholder="Wpisz treść wiadomości..."
            />
          </div>

          {/* Footer */}
          <div className="scc-form-footer">
            <button className="lq-btn" onClick={close}>
              Anuluj
            </button>
            <button className="lq-btn" onClick={delMail}>
              Usuń
            </button>
            <button
              className="lq-btn lq-btn--success"
              onClick={handleSave}
              disabled={saving}
            >
              <Mail size={14} /> {saving ? "Zapisywanie..." : "Zapisz szablon"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMail;
